import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { isRupturaAcionavel } from '../lib/replenishment'
import type { KanbanStatusOp } from '../types'

export type AbcCurve = 'A' | 'B' | 'C' | null
export type StockStatus = 'normal' | 'ruptura' | 'excesso' | null

export interface EstoqueRow {
  id: string
  sku: string
  name: string
  current_stock: number
  cost_avg: number | null
  sale_price: number | null
  monthly_turnover: number | null
  last_sale_at: string | null
  coverage_days: number | null
  abc_curve: AbcCurve
  stock_status: StockStatus
  kanban_status: KanbanStatusOp
  revenue_window: number | null
  margin_value: number | null
  margin_pct: number | null
  analytics_at: string | null
}

export interface AnalyticsKpis {
  totalSkus: number
  ruptura: number
  excesso: number
  normal: number
  encalhe: number
  inactiveEncalhe: number
  generalEncalheIndex: number   // % — (ativos encalhe + inativos encalhe) / (total ativos + inativos encalhe)
  avgCoverage: number
  totalValue: number
  totalRevenue: number
  totalMargin: number
  avgMarginPct: number | null
  abcBreakdown: { A: { count: number; value: number }; B: { count: number; value: number }; C: { count: number; value: number } }
}

const num = (v: unknown): number | null => (v == null ? null : Number(v))

// Linha crua da RPC fn_stock_analytics (numéricos podem vir como string via PostgREST).
interface StockAnalyticsRaw {
  id: string; sku: string; name: string
  current_stock: unknown; cost_avg: unknown; sale_price: unknown
  monthly_turnover: unknown; coverage_days: unknown
  abc_curve: AbcCurve; stock_status: StockStatus; kanban_status: KanbanStatusOp
  revenue_window: unknown; margin_value: unknown; margin_pct: unknown
}

/** Intervalo de datas para o modo mês-calendário (recorta só as vendas). */
export interface DateRange { start: string; end: string; days: number }

/**
 * Indicadores de estoque calculados AO VIVO via RPC fn_stock_analytics.
 * Sem `range`: janela móvel de `windowDays` (comportamento atual).
 * Com `range`: as métricas de VENDA (receita/CMV/margem/ABC/giro) refletem o mês
 * escolhido; as de ESTOQUE seguem snapshot atual (não há histórico de estoque).
 */
export function useEstoqueAnalytics(windowDays = 180, range?: DateRange) {
  const [rows, setRows] = useState<EstoqueRow[]>([])
  const [inactiveEncalhe, setInactiveEncalhe] = useState(0)
  const [loading, setLoading] = useState(true)
  const [recalculating, setRecalculating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastRun, setLastRun] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)

    // Modo mês: recorta as vendas por [start, end] e usa os dias do mês como divisor de giro.
    const stockArgs = range
      ? { p_days: range.days, p_start: range.start, p_end: range.end }
      : { p_days: windowDays }

    const [rpcRes, salesRes, inactiveEncalheRes] = await Promise.all([
      supabase.rpc('fn_stock_analytics', stockArgs),
      // View agrupada: 1 linha por produto com MAX(sale_date). Evita transferir todas as vendas.
      supabase.from('last_sale_per_product').select('product_id, last_sale_date'),
      // Encalhe é snapshot atual (saldo>0 + sem venda há +N dias) — não recorta por mês.
      supabase.rpc('fn_inactive_encalhe_count', { p_days: windowDays }),
    ])

    if (rpcRes.error) { setError(rpcRes.error.message); setLoading(false); return }
    if (salesRes.error) setError(salesRes.error.message)
    setInactiveEncalhe(typeof inactiveEncalheRes.data === 'number' ? inactiveEncalheRes.data : 0)

    const lastSaleByProduct = new Map<string, string>()
    for (const s of (salesRes.data ?? []) as Array<{ product_id: string; last_sale_date: string }>) {
      lastSaleByProduct.set(s.product_id, s.last_sale_date)
    }

    const list: EstoqueRow[] = ((rpcRes.data ?? []) as StockAnalyticsRaw[]).map(p => ({
      id: p.id,
      sku: p.sku,
      name: p.name,
      current_stock: Number(p.current_stock) || 0,
      cost_avg: num(p.cost_avg),
      sale_price: num(p.sale_price),
      monthly_turnover: num(p.monthly_turnover),
      coverage_days: p.coverage_days == null ? null : Number(p.coverage_days),
      abc_curve: p.abc_curve ?? null,
      stock_status: p.stock_status ?? null,
      kanban_status: p.kanban_status,
      revenue_window: num(p.revenue_window),
      margin_value: num(p.margin_value),
      margin_pct: num(p.margin_pct),
      analytics_at: null,
      last_sale_at: lastSaleByProduct.get(p.id) ?? null,
    }))
    setRows(list)
    setLastRun(new Date().toISOString()) // cálculo ao vivo
    setLoading(false)
  }, [windowDays, range?.start, range?.end, range?.days])

  useEffect(() => { fetchData() }, [fetchData])

  // Cálculo é ao vivo: "recalcular" apenas re-executa a RPC (ex.: após sync de vendas).
  const recalcular = useCallback(async () => {
    setRecalculating(true)
    await fetchData()
    setRecalculating(false)
    return true
  }, [fetchData])

  return { rows, inactiveEncalhe, loading, recalculating, error, lastRun, recalcular, refetch: fetchData }
}

export function computeKpis(rows: EstoqueRow[], inactiveEncalhe = 0, encalheDays = 180): AnalyticsKpis {
  const totalSkus = rows.length
  let ruptura = 0, excesso = 0, normal = 0, encalhe = 0
  let coverageSum = 0, coverageCount = 0
  let totalValue = 0
  let totalRevenue = 0
  let totalMargin = 0
  const abc = {
    A: { count: 0, value: 0 },
    B: { count: 0, value: 0 },
    C: { count: 0, value: 0 },
  }
  const encalheCutoff = Date.now() - encalheDays * 24 * 60 * 60 * 1000
  for (const r of rows) {
    if (isRupturaAcionavel(r.current_stock, r.kanban_status)) ruptura++
    else if (r.stock_status === 'excesso') excesso++
    else if (r.stock_status === 'normal') normal++

    // Encalhe: tem estoque + tem histórico de venda + última venda há mais de 90 dias
    // (produtos sem `last_sale_at` são desconsiderados — podem ser novos no catálogo)
    if (r.current_stock > 0 && r.last_sale_at) {
      const lastSaleMs = new Date(r.last_sale_at).getTime()
      if (lastSaleMs < encalheCutoff) encalhe++
    }

    if (r.coverage_days != null) { coverageSum += r.coverage_days; coverageCount++ }
    const value = (r.current_stock ?? 0) * (r.cost_avg ?? 0)
    totalValue += value
    if (r.abc_curve && r.abc_curve in abc) {
      abc[r.abc_curve].count++
      abc[r.abc_curve].value += value
    }

    if (r.revenue_window != null) totalRevenue += r.revenue_window
    if (r.margin_value   != null) totalMargin  += r.margin_value
  }
  // Índice geral de encalhe: combina ativos + inativos encalhados.
  // (ativos_encalhe + inativos_encalhe) / (total_ativos + inativos_encalhe) × 100
  const genNum = encalhe + inactiveEncalhe
  const genDen = totalSkus + inactiveEncalhe
  const generalEncalheIndex = genDen > 0 ? (genNum / genDen) * 100 : 0

  return {
    totalSkus, ruptura, excesso, normal, encalhe,
    inactiveEncalhe,
    generalEncalheIndex,
    avgCoverage: coverageCount > 0 ? Math.round(coverageSum / coverageCount) : 0,
    totalValue,
    totalRevenue,
    totalMargin,
    avgMarginPct: totalRevenue > 0 ? (totalMargin / totalRevenue) * 100 : null,
    abcBreakdown: abc,
  }
}
